import { gql, useMutation } from '@apollo/client';
import IconGitHubSolid from '@apollo/icons/small/IconGitHubSolid.svg';
import { Avatar } from '@apollo/orbit';
import classnames from 'classnames';
import React from 'react';

import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { ClickableText } from '../common/clickableText/ClickableText';
import { PermissionGuard } from '../permissionGuards/permissionGuard/PermissionGuard';

const RESEND_INVITATION_MUTATION = gql`
  mutation UI__resendInvitationMutation($accountId: ID!, $inviteId: ID!) {
    account(id: $accountId) {
      resendInvitation(id: $inviteId) {
        id
        lastSentAt
      }
    }
  }
`;

interface Props {
  accountId: string;
  avatarUrl: string | null;
  fullName: React.ReactNode;
  email: string | null;
  ghUsername: string | null;
  inviteId?: string;
  selected?: boolean;
  label?: string;
  hasMetMaxSeatLimit?: boolean;
}

export const FullNameColumn = ({
  ghUsername,
  accountId,
  inviteId,
  avatarUrl,
  fullName,
  email,
  selected = false,
  label,
  hasMetMaxSeatLimit,
}: Props) => {
  const [resendInvite, { error, loading, called }] = useMutation<
    GraphQLTypes.UI__resendInvitationMutation,
    GraphQLTypes.UI__resendInvitationMutationVariables
  >(RESEND_INVITATION_MUTATION);

  return (
    <div className="flex text-left">
      <Avatar size="sm" src={avatarUrl} />
      <div className="ml-2 flex flex-col items-start justify-center truncate text-base">
        <div className="leading-none">
          {fullName}
          {ghUsername && (
            <a
              href={`https://github.com/${ghUsername}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <IconGitHubSolid className="ml-2 cursor-pointer text-icon-secondary" />
            </a>
          )}
          {label && (
            <span className="ml-2 inline-flex items-center rounded border border-primary px-1 text-xs font-semibold uppercase  tracking-wide text-neutral">
              {label}
            </span>
          )}
        </div>
        {(email || inviteId) && (
          <div
            className={classnames(
              'text-sm',
              selected ? 'text-inverted' : 'text-secondary',
            )}
          >
            <span className="mr-1">{email}</span>
            {inviteId && (
              <PermissionGuard
                accountId={accountId}
                accountPermissions="canManageMembers"
                fallback={null}
              >
                {called && !loading ? (
                  error ? (
                    <span className="text-red">
                      There was an error sending the invitation, please refresh
                      and try again.
                    </span>
                  ) : (
                    'Invite Sent!'
                  )
                ) : (
                  !hasMetMaxSeatLimit && (
                    <ClickableText
                      className={classnames(
                        'focus:outline-none',
                        loading ? '' : 'text-link',
                      )}
                      onClick={() =>
                        resendInvite({ variables: { accountId, inviteId } })
                      }
                    >
                      Resend Invite
                    </ClickableText>
                  )
                )}
              </PermissionGuard>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
